import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';
import { API_BASE } from './lobbyReducer';
import { CANVAS_W } from './constants';

// ── SVG clip path — trapezoid shape (matches old EngBoothClipDef exactly) ─────
export function EngBoothClipDef() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute', overflow: 'hidden' }}>
      <defs>
        <clipPath id="eng-booth-clip" clipPathUnits="objectBoundingBox">
          <path d="M0 0.9 Q0 1 0.1 1 L0.9 1 Q1 1 1 0.9 L0.88 0.1 Q0.88 0 0.78 0 L0.22 0 Q0.12 0 0.12 0.1 Z" />
        </clipPath>
      </defs>
    </svg>
  );
}

// ── Tile appear animation ─────────────────────────────────────────────────────
const scaleIn = keyframes`
  from { transform: scale(0); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
`;
const tileAppearSx = (i) => ({
  animation: `${scaleIn} 0.28s ease-out both`,
  animationDelay: `${i * 38}ms`,
});
const tileTransition = 'transform 0.22s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.18s ease';

// ── Booth thumbnail ───────────────────────────────────────────────────────────
function BoothThumbnail({ exhibitor, size }) {
  const boothColor = exhibitor.boothBgColor || '#1a1a2e';
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: boothColor, overflow: 'hidden' }}>
      {exhibitor.boothThumbnail ? (
        <Box component="img" src={exhibitor.boothThumbnail} alt={exhibitor.name}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: exhibitor.boothThumbnailFit || 'cover', display: 'block' }} />
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, rgba(16,117,186,0.25) 0%, ${boothColor} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Inline SVG storefront — matches old version exactly */}
          <svg viewBox="0 0 24 24" width={size * 0.28} height={size * 0.28} fill="rgba(255,255,255,0.15)">
            <path d="M20 4H4v2l8 5 8-5V4zm0 4.236l-8 5-8-5V20h16V8.236z"/>
          </svg>
        </Box>
      )}
    </Box>
  );
}

// ── Logo / name footer ────────────────────────────────────────────────────────
function BoothFooter({ exhibitor, nameFontSize, flat }) {
  return (
    <Box sx={{
      width: '100%', height: '20%',
      bgcolor: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      px: '6%', overflow: 'hidden',
      ...(flat ? { borderTop: '1px solid #f0f4f8' } : {}),
    }}>
      {exhibitor.logoUrl ? (
        <Box component="img" src={exhibitor.logoUrl} alt={exhibitor.name}
          sx={{ maxHeight: '70%', maxWidth: '85%', objectFit: 'contain', display: 'block' }} />
      ) : (
        <Typography sx={{
          fontSize: nameFontSize, fontWeight: 700, color: '#1075BA',
          textAlign: 'center', lineHeight: 1.2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          px: 0.25, width: '100%',
        }}>
          {exhibitor.name}
        </Typography>
      )}
    </Box>
  );
}

// ── Single booth card ─────────────────────────────────────────────────────────
// size is the resolved CSS px size of the square cell.
// onClick receives the exhibitor object (card click → transition logic in parent).
export function EngBoothCard({ exhibitor, size, onClick, cardRef, cardTemplate = 'masked' }) {
  const [hovered, setHovered] = useState(false);

  const boothAction = exhibitor.boothAction;
  const destType    = boothAction?.type || (exhibitor.boothLobbyId ? 'lobby' : null);
  const clickable   = !!destType;

  const effectiveTile = size > 0 ? size : 110;
  const nameFontSize  = Math.round(effectiveTile * 0.076);

  return (
    <Box
      ref={cardRef}
      onClick={clickable ? () => onClick(exhibitor) : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative', aspectRatio: '1',
        ...(size > 0 ? { width: size, height: size } : {}),
        cursor:        clickable ? 'pointer' : 'default',
        transform:     hovered ? 'scale(1.07)' : 'scale(1)',
        transition:    'transform 0.2s cubic-bezier(0.34,1.56,0.64,1)',
        transformOrigin: 'center center',
        zIndex:        hovered ? 10 : 1,
      }}
    >
      {cardTemplate === 'flat' ? (
        // ── Flat card ────────────────────────────────────────────────────────
        <Box sx={{ width: '100%', height: '100%', borderRadius: '10px', overflow: 'hidden', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
          <Box sx={{ position: 'relative', width: '100%', height: '80%', overflow: 'hidden' }}>
            <BoothThumbnail exhibitor={exhibitor} size={size} />
          </Box>
          <BoothFooter exhibitor={exhibitor} nameFontSize={nameFontSize} flat={true} />
        </Box>
      ) : (
        // ── Masked card (default) ─────────────────────────────────────────────
        <Box sx={{ width: '100%', height: '100%', clipPath: 'url(#eng-booth-clip)', overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', width: '100%', height: '80%', overflow: 'hidden' }}>
            <BoothThumbnail exhibitor={exhibitor} size={size} />
          </Box>
          <BoothFooter exhibitor={exhibitor} nameFontSize={nameFontSize} flat={false} />
        </Box>
      )}
    </Box>
  );
}

// ── EngExhibitorGrid ──────────────────────────────────────────────────────────
export function EngExhibitorGrid({
  hotspot,
  onNavigate,
  onNavigateSlide,
  onCommitLobby,
  canvasScale = 1,
  canvasRef,
  bp = 'desktop',
}) {
  const isActive = hotspot.isActive !== false;
  if (!isActive) return null;

  const x = hotspot.points?.[0]?.x ?? 10;
  const y = hotspot.points?.[0]?.y ?? 10;
  const w = hotspot.meta?.width    ?? 40;
  const h = hotspot.meta?.height   ?? 22.5;

  // ── Breakpoint-aware defaults ─────────────────────────────────────────────
  const isNarrowBp      = bp === 'tablet-portrait' || bp === 'mobile';
  const tileSizeDefault = isNarrowBp ? 450 : 250;
  const gapDefault      = isNarrowBp ? 40  : 8;

  const rawTileSize  = hotspot.meta?.gridTileSize     ?? tileSizeDefault;
  const rawGap       = hotspot.meta?.gridGap          ?? gapDefault;
  const cardTemplate = hotspot.meta?.gridCardTemplate ?? 'masked';
  const displayMode  = hotspot.meta?.gridDisplayMode  ?? 'paged';
  const lId          = hotspot.meta?.lobbyId          || null;

  // ── State ─────────────────────────────────────────────────────────────────
  const [exhibitors,  setExhibitors]  = useState([]);
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(0);
  const [cols,        setCols]        = useState(2);
  const [rows,        setRows]        = useState(2);
  const [cellSize,    setCellSize]    = useState(null); // resolved CSS px tile size
  const [gapPx,       setGapPx]       = useState(8);
  const [gridOpacity, setGridOpacity] = useState(1);

  const containerRef = useRef(null);
  const searchBarRef = useRef(null);
  const navBarRef    = useRef(null);
  const trackRef     = useRef(null);
  const dragRef      = useRef({ dragging: false, startX: 0, startPage: 0 });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!lId) return;
    fetch(`${API_BASE}/exhibitors/public-by-lobby/${lId}`)
      .then(r => r.ok ? r.json() : null)
      .then(json => { if (json?.success) setExhibitors([...json.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))); })
      .catch(() => {});
  }, [lId]);

  // ── ResizeObserver — desktopScale formula for correct col counts ──────────
  useEffect(() => {
    const compute = () => {
      if (!containerRef.current) return;
      const totalW  = containerRef.current.clientWidth;
      const totalH  = containerRef.current.clientHeight;
      const searchH = searchBarRef.current?.offsetHeight ?? 0;
      const navH    = isScroll ? 0 : (navBarRef.current?.offsetHeight ?? 0);

      // rawTileSize/rawGap stored in desktop canvas units (1440) — scale to CSS px
      const ds         = totalW / CANVAS_W;
      const newTilePx  = rawTileSize > 0 ? Math.round(rawTileSize * ds) : 0;
      const newGapPx   = Math.max(1, Math.round(rawGap * ds));

      const availW = totalW - newGapPx * 2;
      const availH = totalH - searchH - navH - newGapPx * 2;
      if (availW <= 0 || availH <= 0) return;

      const resolved = newTilePx > 0 ? newTilePx : Math.floor(Math.min(availW / 3, availH / 2));
      const newCols  = Math.max(1, Math.floor((availW + newGapPx) / (resolved + newGapPx)));
      const newRows  = Math.max(1, Math.floor((availH + newGapPx) / (resolved + newGapPx)));

      setCols(newCols);
      setRows(newRows);
      setCellSize(resolved);
      setGapPx(newGapPx);
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [rawTileSize, rawGap, displayMode]);

  // resolvedSize: the final CSS px used for each square card
  const resolvedSize = cellSize ?? 0;

  // ── Derived display ───────────────────────────────────────────────────────
  const filtered = search.trim()
    ? exhibitors.filter(ex => ex.name?.toLowerCase().includes(search.toLowerCase()))
    : exhibitors;

  const isScroll = displayMode === 'scroll';
  const perPage  = isScroll ? Infinity : cols * rows;
  const pages    = isScroll ? 1 : Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, Math.max(0, pages - 1));

  useEffect(() => { setPage(0); }, [search]);

  // ── Grid CSS ──────────────────────────────────────────────────────────────
  // Uses CSS Grid — matches builder gridSx exactly.
  // tilePx > 0: fixed column size; otherwise colPct fills available width.
  const tilePx = resolvedSize;
  const colPct = `calc(${100 / cols}% - ${gapPx * (cols - 1) / cols}px)`;

  const gridSx = {
    display:             'grid',
    gridTemplateColumns: tilePx > 0 ? `repeat(${cols}, ${tilePx}px)` : `repeat(${cols}, ${colPct})`,
    gap:                 `${gapPx}px`,
    alignContent:        isScroll ? 'start' : 'center',
    justifyContent:      'center',
    width:               '100%',
    flex:                isScroll ? undefined : 1,
    px:                  `${gapPx}px`,
    pb:                  `${gapPx}px`,
    boxSizing:           'border-box',
    overflow:            isScroll ? 'visible' : 'hidden',
    userSelect:          'none',
  };

  // ── Track position sync (paged mode) ─────────────────────────────────────
  // When safePage changes via arrow/dot (not drag), animate track to new position.
  useEffect(() => {
    if (!trackRef.current || isScroll) return;
    trackRef.current.style.transition = 'transform 0.35s cubic-bezier(0.4,0,0.2,1)';
    trackRef.current.style.transform  = `translateX(${-safePage * 100}%)`;
    const t = setTimeout(() => { if (trackRef.current) trackRef.current.style.transition = ''; }, 360);
    return () => clearTimeout(t);
  }, [safePage, isScroll]);

  // ── Drag handlers (paged mode — mouse + touch) ────────────────────────────
  const onDragStart = (e) => {
    if (isScroll) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    dragRef.current = { dragging: true, startX: clientX, startPage: safePage };
    e.stopPropagation();
  };
  const onDragMove = (e) => {
    if (!dragRef.current.dragging || !trackRef.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const dx = clientX - dragRef.current.startX;
    const trackW = trackRef.current.clientWidth;
    trackRef.current.style.transition = 'none';
    trackRef.current.style.transform  = `translateX(${(-safePage + dx / trackW) * 100}%)`;
    e.stopPropagation();
  };
  const onDragEnd = (e) => {
    if (!dragRef.current.dragging || !trackRef.current) return;
    const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const dx        = clientX - dragRef.current.startX;
    const threshold = trackRef.current.clientWidth * 0.20;
    let nextPage    = dragRef.current.startPage;
    if      (dx < -threshold) nextPage = Math.min(pages - 1, nextPage + 1);
    else if (dx >  threshold) nextPage = Math.max(0, nextPage - 1);
    dragRef.current.dragging = false;
    trackRef.current.style.transition = 'transform 0.35s cubic-bezier(0.4,0,0.2,1)';
    trackRef.current.style.transform  = `translateX(${-nextPage * 100}%)`;
    setTimeout(() => { if (trackRef.current) trackRef.current.style.transition = ''; }, 360);
    setPage(nextPage);
    e.stopPropagation();
  };

  // ── Card click → transition overlay ──────────────────────────────────────
  const handleCardClick = useCallback((exhibitor) => {
    const boothAction  = exhibitor.boothAction;
    const destType     = boothAction?.type || (exhibitor.boothLobbyId ? 'lobby' : null);
    const destTargetId = boothAction?.targetId || exhibitor.boothLobbyId || null;
    if (!destType || !destTargetId) return;

    // Slide navigation — no overlay, just navigate
    if (destType === 'slide') {
      onNavigateSlide?.(destTargetId);
      return;
    }

    // Lobby navigation — fade grid out then navigate
    setGridOpacity(0);
    setTimeout(() => {
      onNavigate?.(String(destTargetId), exhibitor.transitionVideoUrl || null);
    }, 200);
  }, [onNavigate, onNavigateSlide]);


  // ── Search / nav scaled sizes ─────────────────────────────────────────────
  const scaledSearchFontSize  = Math.round(18    * canvasScale);
  const scaledSearchIcon      = Math.round(20.6  * canvasScale);
  const scaledSearchClear     = Math.round(18.75 * canvasScale);
  const scaledSearchPx        = Math.round(10    * canvasScale);
  const scaledSearchPy        = Math.round(6     * canvasScale);
  const scaledSearchRadius    = Math.round(20    * canvasScale);
  const scaledSearchMaxW      = Math.round(420   * canvasScale);
  const scaledNavBtn          = Math.round(33    * canvasScale);
  const scaledNavIcon         = Math.round(24    * canvasScale);
  const scaledDotActive       = Math.round(27    * canvasScale);
  const scaledDot             = Math.round(10.5  * canvasScale);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        left: `${x}%`, top: `${y}%`,
        width: `${w}%`, height: `${h}%`,
        zIndex:        5 + (hotspot.order ?? 0),
        overflow:      'hidden',
        display:       'flex',
        flexDirection: 'column',
        opacity:       gridOpacity,
        transition:    gridOpacity === 0 ? 'opacity 0.2s ease' : 'none',
        userSelect:    'none',
      }}
    >
      {/* Clip def always rendered so overlay clone can reference it */}
      <EngBoothClipDef />

      {/* ── Search bar ── */}
      <Box
        ref={searchBarRef}
        onMouseDown={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
        sx={{ flexShrink: 0, px: `${gapPx}px`, pt: `${gapPx}px`, pb: `${gapPx / 2}px`, display: 'flex', justifyContent: 'center' }}
      >
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          width: '100%', maxWidth: `${scaledSearchMaxW}px`,
          background:           'rgba(10,14,26,0.72)',
          backdropFilter:       'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderRadius:         `${scaledSearchRadius}px`,
          px: `${scaledSearchPx}px`, py: `${scaledSearchPy}px`,
          border:    '1px solid rgba(255,255,255,0.18)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
          '&:focus-within': { borderColor: 'rgba(255,255,255,0.55)', background: 'rgba(10,14,26,0.85)' },
        }}>
          <SearchOutlined sx={{ fontSize: scaledSearchIcon, color: '#fff', flexShrink: 0, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' }} />
          <Box
            component="input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            placeholder="Search exhibitors…"
            sx={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: scaledSearchFontSize, color: '#fff', fontFamily: 'inherit',
              textShadow: '0 1px 3px rgba(0,0,0,0.7)',
              '&::placeholder': { color: 'rgba(255,255,255,0.55)' },
            }}
          />
          {search && (
            <Box
              onMouseDown={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              onClick={() => setSearch('')}
              sx={{ cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: scaledSearchClear, lineHeight: 1, '&:hover': { color: '#fff' } }}
            >✕</Box>
          )}
        </Box>
      </Box>

      {/* ── Scroll mode ── */}
      {isScroll && (
        <Box
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          onTouchMove={e => e.stopPropagation()}
          sx={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.3)', borderRadius: 2 },
          }}
        >
          {resolvedSize > 0 && (
            <Box sx={{ ...gridSx, pt: `${gapPx}px`, pb: `${gapPx}px` }}>
              {filtered.map((ex, i) => (
                <Box key={ex._id} sx={{ transition: tileTransition, willChange: 'transform', ...tileAppearSx(i) }}>
                  <EngBoothCard
                    exhibitor={ex} size={resolvedSize}
                    onClick={handleCardClick}
                    cardTemplate={cardTemplate}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ── Paged carousel mode ───────────────────────────────────────────── */}
      {/* All pages pre-rendered in a horizontal track. Drag moves the track  */}
      {/* in real-time; arrows/dots animate it via CSS transition.            */}
      {!isScroll && (
        <>
          <Box
            onMouseDown={onDragStart}
            onMouseMove={onDragMove}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchStart={onDragStart}
            onTouchMove={onDragMove}
            onTouchEnd={onDragEnd}
            sx={{
              flex: 1, overflow: 'hidden', position: 'relative',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            {/* Track: all pages side-by-side, each page = 100% container width */}
            <Box
              ref={trackRef}
              sx={{ display: 'flex', width: '100%', height: '100%', willChange: 'transform' }}
            >
              {resolvedSize > 0 && Array.from({ length: pages }).map((_, pi) => {
                const pageItems = filtered.slice(pi * perPage, pi * perPage + perPage);
                // Pad last row with invisible filler cells so CSS Grid centres every row.
                // Without fillers an incomplete last row left-aligns within the centered block.
                const remainder = pageItems.length % cols;
                const fillers   = remainder === 0 ? 0 : cols - remainder;
                return (
                  <Box key={pi} sx={{ ...gridSx, minWidth: '100%', height: '100%', alignContent: 'center', pt: `${gapPx}px` }}>
                    {pageItems.map((ex, i) => (
                      <Box key={ex._id} sx={{ transition: tileTransition, willChange: 'transform', ...tileAppearSx(i) }}>
                        <EngBoothCard
                          exhibitor={ex} size={resolvedSize}
                          onClick={handleCardClick}
                          cardTemplate={cardTemplate}
                        />
                      </Box>
                    ))}
                    {Array.from({ length: fillers }).map((_, fi) => (
                      <Box key={`filler-${fi}`} sx={{ ...(resolvedSize > 0 ? { width: resolvedSize, height: resolvedSize } : {}), visibility: 'hidden', pointerEvents: 'none' }} />
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Pagination — arrows + dots */}
          {pages > 1 && (
            <Box
              ref={navBarRef}
              onMouseDown={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, pb: `${gapPx}px`, pt: `${gapPx / 2}px` }}
            >
              {/* Left arrow */}
              <Box
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{
                  width: scaledNavBtn, height: scaledNavBtn, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background:           safePage > 0 ? 'rgba(10,14,26,0.72)' : 'rgba(10,14,26,0.35)',
                  backdropFilter:       'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
                  border:               '1px solid rgba(255,255,255,0.18)',
                  boxShadow:            safePage > 0 ? '0 2px 8px rgba(0,0,0,0.35)' : 'none',
                  cursor:               safePage > 0 ? 'pointer' : 'default',
                  pointerEvents:        safePage > 0 ? 'auto' : 'none',
                  color:                safePage > 0 ? '#fff' : 'rgba(255,255,255,0.3)',
                  transition:           'all 0.15s',
                  '&:hover': safePage > 0 ? { background: '#0d7a5f', borderColor: '#0d7a5f', boxShadow: '0 2px 12px rgba(13,122,95,0.4)' } : {},
                }}
              >
                <svg viewBox="0 0 24 24" width={scaledNavIcon} height={scaledNavIcon} fill="currentColor">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                </svg>
              </Box>

              {/* Dots */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {Array.from({ length: pages }).map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => setPage(i)}
                    sx={{
                      width:          i === safePage ? scaledDotActive : scaledDot,
                      height:         scaledDot,
                      borderRadius:   99,
                      background:     i === safePage ? '#fff' : 'rgba(10,14,26,0.55)',
                      border:         '1px solid rgba(255,255,255,0.4)',
                      backdropFilter: 'blur(4px)',
                      cursor:         'pointer',
                      transition:     'all 0.25s',
                      boxShadow:      i === safePage ? '0 1px 4px rgba(0,0,0,0.4)' : 'none',
                    }}
                  />
                ))}
              </Box>

              {/* Right arrow */}
              <Box
                onClick={() => setPage(p => Math.min(pages - 1, p + 1))}
                sx={{
                  width: scaledNavBtn, height: scaledNavBtn, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background:           safePage < pages - 1 ? 'rgba(10,14,26,0.72)' : 'rgba(10,14,26,0.35)',
                  backdropFilter:       'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
                  border:               '1px solid rgba(255,255,255,0.18)',
                  boxShadow:            safePage < pages - 1 ? '0 2px 8px rgba(0,0,0,0.35)' : 'none',
                  cursor:               safePage < pages - 1 ? 'pointer' : 'default',
                  pointerEvents:        safePage < pages - 1 ? 'auto' : 'none',
                  color:                safePage < pages - 1 ? '#fff' : 'rgba(255,255,255,0.3)',
                  transition:           'all 0.15s',
                  '&:hover': safePage < pages - 1 ? { background: '#0d7a5f', borderColor: '#0d7a5f', boxShadow: '0 2px 12px rgba(13,122,95,0.4)' } : {},
                }}
              >
                <svg viewBox="0 0 24 24" width={scaledNavIcon} height={scaledNavIcon} fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </Box>
            </Box>
          )}
        </>
      )}

    </Box>
  );
}